import * as React from 'react'
import { OnChangeValue } from 'react-select'
import Select from '../../components/Select'

import DebouncedTextField from '../../components/DebouncedTextField'
import Loader from '../../components/Loader'

import {
  Box,
  InputLabel,
  InputAdornment,
  Icon,
  Paper,
  Divider,
} from '@design/pt-ui'

import { Project } from '../../interfaces/entities/project'
import { Client } from '../../interfaces/entities/client'

import DI from '../../app/di'
import { UserGroups } from '../../interfaces/services/user'
import { translateWithUserLocale } from '../../utils/i18n'
import { IProjectService } from '../../interfaces/services/projects'
import { AdvertiserUser } from '../../interfaces/entities/user'

interface IFilterState {
  clients: Client[] | null
  projects: Project[] | null
  selectedClient: Client | null
  selectedProjects: Project[]
  advertiserUsers: AdvertiserUser[]
  selectedUser: AdvertiserUser | null
  copyFromUser: AdvertiserUser | null
  projectsForUser: Project[] | null

  query: string
  updating: boolean
}

const getProjectsForClient = (service: IProjectService, client: Client) => {
  const statuses = [
    'work_in_progress',
    'specification_finalized',
    'sent_to_agency',
    'open',
  ].map((x) => {
    return {
      name: x,
      description: x,
      title: x,
    }
  })

  return service.searchProjects(
    {
      clients: [client],
      statuses: statuses,
      query: '',
      favorite: false,
      pageNo: null,
    },
    {
      resultsPerPage: 9999,
      pageNo: 1,
      ordering: {
        columnName: 'deadline',
        direction: 'asc',
      },
    },
  )
}

export function useAdminFilter() {
  const getProjectService = () => {
    return DI.get('ProjectService') as IProjectService
  }

  const projectService = getProjectService()

  const [state, setState] = React.useState<IFilterState>({
    clients: null,
    projects: null,
    projectsForUser: null,

    selectedProjects: [],
    selectedClient: null,
    copyFromUser: null,
    advertiserUsers: [],
    selectedUser: null,
    query: '',
    updating: false,
  })

  const filteredUsers = state.advertiserUsers.filter((au) => {
    if (state.query === '') {
      return true
    }
    const query = state.query.toLowerCase()
    const fullName = `${au.firstName} ${au.lastName}`
    return (
      fullName.toLowerCase().includes(query) ||
      au.username.toLowerCase().includes(query) ||
      au.email.toLowerCase().includes(query)
    )
  })

  React.useEffect(() => {
    Promise.all([getProjectService().getClients()]).then(([clients]) => {
      if (clients.type === 'error') {
        console.warn('Error loading clients')
      }

      if (clients.type === 'success') {
        if (clients.payload.length === 1) {
          setState((state) => ({
            ...state,
            clients: clients.payload,
            selectedClient: clients.payload[0],
          }))
        } else {
          setState((state) => ({
            ...state,
            clients: clients.payload,
            selectedClient: null,
          }))
        }
      }
    })
  }, [])

  const handleClientChange = (
    selected: OnChangeValue<{ value: string; label: string }, boolean>,
  ) => {
    let client: Client | null = null

    if (selected) {
      // @ts-ignore
      const id = selected.value
      client = (state.clients || []).find((c) => c.id === id) || null
    }

    setState((state) => ({
      ...state,
      selectedClient: client,
    }))
  }

  const handleSearch = (query: string) => {
    setState((state) => ({
      ...state,
      query,
    }))
  }

  React.useEffect(() => {
    if (state.selectedClient) {
      setState((state) => ({
        ...state,
        query: '',
        advertiserUsers: state.selectedClient?.advertiserUsers || [],
        selectedUser: null,
      }))
    } else {
      setState((state) => ({
        ...state,
        query: '',
        advertiserUsers: [],
        selectedUser: null,
      }))
    }
  }, [state.selectedClient])

  React.useEffect(() => {
    const client = state.selectedClient
    if (client === null) {
      setState((state) => ({
        ...state,
        projects: null,
      }))
      return
    }

    let alive = true
    const fetch = async () => {
      const result = await getProjectsForClient(getProjectService(), client)

      if (result.type === 'success') {
        return result.payload.projects
      } else {
        return []
      }
    }

    setState((state) => ({
      ...state,
      projects: null,
    }))
    fetch().then((projects) => {
      if (alive) {
        setState((state) => ({
          ...state,
          projects,
        }))
      }
    })

    return () => {
      alive = false
    }
  }, [state.selectedClient])

  const handleUserSelect = (user: AdvertiserUser) => {
    setState((state) => ({
      ...state,
      selectedUser: user,
    }))
  }

  const handleCopyFromUserChange = (
    selected: OnChangeValue<{ value: string; label: string }, boolean>,
  ) => {
    if (selected) {
      // @ts-ignore
      const user = state.advertiserUsers.find((u) => u.id === selected.value)

      if (user) {
        setState((state) => ({
          ...state,
          copyFromUser: user,
        }))
      }
    } else {
      setState((state) => ({
        ...state,
        copyFromUser: null,
      }))
    }
  }

  const handleProjectChange = (
    projects: OnChangeValue<{ value: any; label: string }, boolean>,
  ) => {
    const selectedProjects: Project[] = []

    if (projects !== null) {
      // @ts-ignore
      projects.forEach(({ value: projectId }) => {
        const project = state.projects?.find((p) => p.id === projectId)
        if (project) {
          selectedProjects.push(project)
        }
      })
    }

    setState((state) => ({
      ...state,
      selectedProjects,
    }))
  }

  React.useEffect(() => {
    if (state.advertiserUsers.length === 0) {
      setState((state) => ({
        ...state,
        selectedUser: null,
      }))
    }
  }, [state.advertiserUsers])

  React.useEffect(() => {
    let alive = true
    const fetch = async () => {
      if (state.selectedUser && state.selectedClient) {
        setState((state) => ({
          ...state,
          projectsForUser: null,
          copyFromUser: null,
        }))
        const projects = await projectService.getProjectsUserHasAccessTo(
          state.selectedUser.id,
          state.selectedClient.id,
        )
        if (alive) {
          if (projects.type === 'success') {
            setState((state) => ({
              ...state,
              projectsForUser: projects.payload,
              selectedProjects: projects.payload,
            }))
          }
        }
      }
    }

    if (state.selectedUser !== null && state.selectedUser.restrictedAccess) {
      fetch()
    }
    return () => {
      alive = false
    }
  }, [state.selectedUser]) // eslint-disable-line react-hooks/exhaustive-deps

  const copyPermissions = async () => {
    if (state.selectedUser && state.copyFromUser && state.selectedClient) {
      setState((state) => ({
        ...state,
        updating: true,
      }))

      const response = await projectService.copyProjectPermissionsFromUser(
        state.copyFromUser,
        state.selectedUser,
        state.selectedClient.id,
      )
      if (response.type === 'success') {
        setState((state) => ({
          ...state,
          updating: false,
          selectedUser: response.payload.user,
          copyFromUser: null,
        }))
      } else {
        setState((state) => ({
          ...state,
          updating: false,
        }))
      }
    }
  }

  const assignProjects = async () => {
    if (state.selectedUser && state.selectedClient) {
      setState((state) => ({
        ...state,
        updating: true,
      }))

      const response = await projectService.assignProjectAccess(
        state.selectedUser,
        state.selectedProjects,
        state.selectedClient.id,
      )
      if (response.type === 'success') {
        const { projects } = response.payload
        setState((state) => ({
          ...state,
          updating: false,
          selectedProjects: projects,
          projectsForUser: projects,
        }))
      } else {
        setState((state) => ({
          ...state,
          updating: false,
        }))
      }
    }
  }

  return {
    state: {
      ...state,
      users: filteredUsers,
    },

    handleSearch,
    handleUserSelect,

    handleClientChange,
    handleProjectChange,
    handleCopyFromUserChange,

    copyPermissions,
    assignProjects,
  }
}

export type AdminFilterHook = ReturnType<typeof useAdminFilter>

interface IFilterProps {
  userGroups: UserGroups
  adminHook: AdminFilterHook
}

function Filter(props: IFilterProps) {
  const { state, handleClientChange, handleSearch } = props.adminHook

  const clientToOption = (client: Client) => {
    return {
      value: client.id,
      label: client.name,
    }
  }

  if (state.clients === null) {
    return <Loader variant="flex" />
  }

  return (
    <div>
      <Paper sx={{ padding: 0.5, textAlign: 'left', margin: 0.5 }}>
        <Box sx={{ paddingLeft: 2 }}>
          <h3>{translateWithUserLocale('filter')}</h3>
        </Box>
        <Divider component="h3" />
        <Box
          sx={{
            display: 'flex',
            flexFlow: 'row',
            padding: 3,
            alignItems: 'flex-end',
          }}>
          <Box
            sx={{
              display: 'flex',
              flexFlow: 'column',
              marginRight: 3,
              width: '40%',
              textAlign: 'left',
            }}>
            <InputLabel>{translateWithUserLocale('client')}</InputLabel>
            <Select
              isMulti={false}
              isClearable={true}
              onChange={handleClientChange}
              options={state.clients.map(clientToOption)}
              placeholder={translateWithUserLocale('projects.select_client')}
              value={
                state.selectedClient
                  ? clientToOption(state.selectedClient)
                  : undefined
              }
              isDisabled={state.clients.length === 1}
            />
          </Box>
          <Box sx={{ width: '30%', marginBottom: 0 }} className="cp-search">
            <DebouncedTextField
              value={state.query}
              onChange={handleSearch}
              text={{
                type: 'search',
                placeholder: translateWithUserLocale('user.management_search'),
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>search</Icon>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        </Box>
      </Paper>
    </div>
  )
}

export default Filter
